import React from 'react';
import { lbl, inp } from './shared';

function Section({ title, children }) {
  const hasContent = React.Children.toArray(children).some(c => c !== null && c !== false && c !== undefined);
  if (!hasContent) return null;
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        fontSize: 13, fontWeight: 700, color: '#1a3364', letterSpacing: '0.06em',
        textTransform: 'uppercase', borderBottom: '1.5px solid #dbeef8', paddingBottom: 8, marginBottom: 12,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7, fontSize: 14 }}>
      <span style={{ color: '#7a99b8', flex: 1 }}>{label}</span>
      <span style={{ color: '#1a3364', fontWeight: 600, flex: 2, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function yn(val) {
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  return null;
}

function formatHeight(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;

  const oldFormat = raw.match(/^(\d+)'(\d+)"?$/);
  if (oldFormat) return `${oldFormat[1]} ft ${oldFormat[2]} in`;

  const totalInches = Number.parseInt(raw, 10);
  if (!Number.isFinite(totalInches) || totalInches <= 0) return raw;
  return `${Math.floor(totalInches / 12)} ft ${totalInches % 12} in`;
}

const missingListWarning = {
  background: '#fdf6e8', border: '1.5px solid #f0deb0', borderRadius: 8,
  padding: '12px 16px', marginBottom: 20, fontSize: 14, color: '#7a5a00', lineHeight: 1.5,
};

export default function Review({ data, onChange, hasDoctorsStep, hasMedicationsStep }) {
  const fullName = [data.firstName, data.middleName, data.lastName].filter(Boolean).join(' ');
  const fullAddress = [data.address, data.address2, data.city, data.state, data.zip].filter(Boolean).join(', ');
  const mailingIsSame = data.mailingIsSameAsPhysical !== false;
  const fullMailingAddress = mailingIsSame ? null : [data.mailingAddress, data.mailingAddress2, data.mailingCity, data.mailingState, data.mailingZip].filter(Boolean).join(', ');
  const ssnMasked = data.ssn ? '•••-••-' + String(data.ssn).slice(-4) : null;
  const incomeList = [...(data.incomeSource || []), data.otherIncome].filter(Boolean).join(', ');
  const providers = (data.preferredProviders || []).filter(Boolean);
  const activeDoctors = (data.doctors || []).filter(d => d.name);
  const activeMeds = (data.medications || []).filter(m => m.name);
  const listsToCheck = [hasDoctorsStep && 'doctors', hasMedicationsStep && 'prescriptions'].filter(Boolean).join(' and ');

  return (
    <div>
      <p style={{ fontSize: 14, color: '#4a6288', marginTop: 0, marginBottom: 20, lineHeight: 1.5 }}>
        Please review your information before submitting. Use the Back button to make changes.
      </p>

      {listsToCheck && (
        <div style={{
          background: '#f4f8fc', border: '1.5px solid #2a5298', borderRadius: 8,
          padding: '12px 16px', marginBottom: 20, fontSize: 14, color: '#1a3364', lineHeight: 1.5,
        }}>
          <strong>Please check that all of your {listsToCheck} are listed below.</strong> If anything
          is missing, use the Back button to add it before submitting.
        </div>
      )}

      <Section title="Personal Information">
        <Field label="Full Name" value={fullName} />
        <Field label="Date of Birth" value={data.dob} />
        <Field label="SSN" value={ssnMasked} />
        <Field label="Gender" value={data.gender || null} />
        <Field label="Height" value={formatHeight(data.height)} />
        <Field label="Weight" value={data.weight ? `${data.weight} lbs` : null} />
      </Section>

      <Section title="Contact Information">
        <Field label="Physical Address" value={fullAddress} />
        <Field label="County" value={data.county} />
        <Field label="Mailing Address" value={fullMailingAddress} />
        <Field label="Email" value={data.email} />
        <Field label="Phone" value={data.phone} />
      </Section>

      <Section title="Insurance & Coverage">
        <Field label="Medicare Number" value={data.medicareNumber} />
        <Field label="Part A Effective" value={data.partADate} />
        <Field label="Part B Effective" value={data.partBDate} />
        <Field label="Medicaid Number" value={data.medicaidNumber} />
        <Field label="Federal LIS" value={data.federalLIS} />
        <Field label="Married" value={yn(data.isMarried)} />
        <Field label="Estimated Eligibility" value={data.estimatedEligibility || null} />
        <Field label="Veteran" value={yn(data.isVeteran)} />
        <Field label="VA Prescriptions" value={yn(data.vaRx)} />
        <Field label="Current Plan (Part D / Supp / HMO / PPO)" value={yn(data.hasCurrentPlan)} />
        <Field label="Private Insurance" value={yn(data.hasPrivateInsurance)} />
        <Field label="Tobacco User" value={yn(data.isSmoker)} />
        <Field label="Income Source(s)" value={incomeList || null} />
        <Field label="Total Monthly Income" value={data.totalMonthlyIncome ? `$${data.totalMonthlyIncome}` : null} />
        {providers.map((p, i) => <Field key={i} label={`Preferred Insurance Company ${i + 1}`} value={p} />)}
      </Section>

      <Section title="Pharmacy">
        {(data.pharmacies || []).filter(Boolean).map((p, i) => (
          <Field key={i} label={i === 0 ? 'Pharmacy' : `Pharmacy ${i + 1}`} value={typeof p === 'object' ? (p.Pharmacy_Name || p.name || '') : p} />
        ))}
      </Section>

      {hasMedicationsStep && activeMeds.length === 0 && !data.noMedications && (
        <div style={missingListWarning}>
          <strong>No prescriptions added.</strong> If you take any prescriptions, use the Back button to
          return to the Medications step and add each one.
        </div>
      )}

      {activeMeds.length > 0 && (
        <Section title={`Medications (${activeMeds.length})`}>
          {activeMeds.map((m, i) => (
            <div key={i} style={{
              background: '#f4f8fc', borderRadius: 8, padding: '10px 14px',
              marginBottom: 8, fontSize: 14,
            }}>
              <strong style={{ color: '#1a3364' }}>{m.name}</strong>
              {m.strength && <span style={{ color: '#2a5298' }}> — {m.strength.toLowerCase().startsWith(m.name.toLowerCase()) ? m.strength.slice(m.name.length).trim() : m.strength}</span>}
              {m.frequency && (
                <div style={{ color: '#6b8aaa', marginTop: 3, fontSize: 13 }}>{m.frequency}</div>
              )}
            </div>
          ))}
        </Section>
      )}

      {hasDoctorsStep && activeDoctors.length === 0 && !data.noDoctors && (
        <div style={missingListWarning}>
          <strong>No doctors added.</strong> If you see any doctors, use the Back button to return to
          the Doctors step, search for each one, and click their name in the results to add them.
        </div>
      )}

      {activeDoctors.length > 0 && (
        <Section title={`Doctors (${activeDoctors.length})`}>
          {activeDoctors.map((d, i) => (
            <div key={i} style={{
              background: '#f4f8fc', borderRadius: 8, padding: '10px 14px',
              marginBottom: 8, fontSize: 14,
            }}>
              <strong style={{ color: '#1a3364' }}>
                {d.name}{d.credential ? `, ${d.credential}` : ''}
              </strong>
              {d.specialty && <span style={{ color: '#4a6288' }}> · {d.specialty}</span>}
              {(d.city || d.state) && (
                <div style={{ fontSize: 13, color: '#7a99b8', marginTop: 2 }}>
                  {[d.city, d.state].filter(Boolean).join(', ')}
                </div>
              )}
              {d.npi && (
                <div style={{ fontSize: 12, color: '#7a99b8', marginTop: 1 }}>NPI: {d.npi}</div>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* Additional comments */}
      <div style={{ borderTop: '1.5px solid #dbeef8', paddingTop: 20 }}>
        <label style={lbl}>Additional Comments</label>
        <textarea
          value={data.additionalComments}
          onChange={e => onChange('additionalComments', e.target.value)}
          rows={5}
          placeholder="Any additional information you'd like us to know…"
          style={{
            ...inp,
            resize: 'vertical',
            lineHeight: 1.5,
          }}
        />
      </div>
    </div>
  );
}
